/**
 * El filtro de Producto de las creaciones: qué productos lista, con qué foto y cómo
 * se acota. Lo usan Creaciones (`GeneratedImagesHistory`) y el selector de creaciones
 * de un lanzamiento (`FiltroDeProducto`), y vive acá para que las dos listas no se
 * separen (ADR-2026-10-20-filtro-de-producto-en-el-selector-de-creaciones).
 *
 * "Producto" para el usuario es la fila de `variantes` —es lo que elige en el
 * catálogo y lo que se muestra al pie de cada card—, así que el filtro va por
 * `variante_id` (ADR-2026-09-04-filtro-por-producto-en-creaciones).
 */
import { supabase } from "./supabase";
import { fetchInChunks } from "../utils/supabaseChunkedIn";
import { getCachedSignedUrl, setCachedSignedUrl } from "../utils/imageCache";

export interface ProductoDelFiltro {
  id: string;
  nombre: string;
  brandId: string | null;
  // Foto del producto para la preview del filtro: `imagenKey` es el valor
  // crudo de `productos_imagenes.url` (nombre de archivo, o URL http en los
  // productos viejos) y `imagenVarianteId` la carpeta donde vive en storage.
  imagenKey: string | null;
  imagenVarianteId: string | null;
}

/**
 * Los productos de la empresa, con la marca de su producto padre y su foto.
 *
 * Tira si no se pudo leer la lista: "no hay productos" y "no se pudieron leer" no son lo
 * mismo, y el desplegable tiene que poder ofrecer reintentar. Las FOTOS, en cambio, son
 * un adorno: si fallan, la lista sale igual, con la caja gris en cada producto (review
 * Greptile #194).
 */
export async function fetchProductosDelFiltro(companyId: string): Promise<ProductoDelFiltro[]> {
  // Traemos también la marca del producto padre: el desplegable de
  // Producto se acota a la marca elegida
  // (ADR-2026-09-04-filtro-por-producto-en-creaciones).
  const { data: variantsData, error } = await supabase
    .from("variantes")
    .select("id, nombre, productos(brand_id)")
    .eq("id_company", companyId)
    .order("nombre");
  if (error) throw error;
  // Foto de cada producto para la preview del filtro. En lotes: con
  // catálogos grandes la lista de `variante_id` en la URL revienta el
  // gateway REST de Supabase (ver utils/supabaseChunkedIn).
  const idsVariantes = (variantsData || []).map((v: any) => v.id);
  let imagenesProducto: any[] = [];
  try {
    imagenesProducto = idsVariantes.length
      ? await fetchInChunks<any>(idsVariantes, (chunk) =>
          supabase
            .from("productos_imagenes")
            .select("url, principal, orden, variante_id")
            .eq("id_company", companyId)
            .in("variante_id", chunk),
        )
      : [];
  } catch (e) {
    console.error("[productosDelFiltro] fotos de producto", e);
  }
  // Una por producto: la principal, si no la de menor `orden`.
  const imagenPorVariante: Record<string, any> = {};
  (imagenesProducto || []).forEach((img: any) => {
    const k = String(img.variante_id);
    const actual = imagenPorVariante[k];
    if (
      !actual ||
      (img.principal && !actual.principal) ||
      (!actual.principal && (img.orden ?? 99) < (actual.orden ?? 99))
    ) {
      imagenPorVariante[k] = img;
    }
  });
  return (variantsData || []).map((v: any) => {
    const prod = Array.isArray(v.productos) ? v.productos[0] : v.productos;
    const img = imagenPorVariante[String(v.id)];
    return {
      id: v.id,
      nombre: v.nombre,
      brandId: prod?.brand_id ?? null,
      imagenKey: img?.url ?? null,
      imagenVarianteId: img ? String(img.variante_id) : null,
    };
  });
}

/**
 * Con una marca elegida la lista se acota a esa marca, dejando siempre los productos
 * sin marca asignada: son de la empresa y no deben desaparecer del desplegable.
 */
export function productosDeMarca<T extends { brandId: string | null }>(lista: T[], brandId: string | null): T[] {
  return lista.filter((v) => (brandId ? v.brandId === brandId || v.brandId == null : true));
}

/** El buscador de adentro del desplegable: por nombre. */
export function buscarEnProductos<T extends { nombre: string }>(lista: T[], texto: string): T[] {
  const q = texto.trim().toLowerCase();
  if (!q) return lista;
  return lista.filter((v) => v.nombre?.toLowerCase().includes(q));
}

/**
 * Miniaturas del filtro: se firman al ABRIR la lista y sólo las que se van a
 * ver, no al cargar la pantalla. Las fotos de producto son privadas y firmar
 * un catálogo entero de arranque serían cientos de URLs que casi nunca se
 * miran. Se piden `opt/` y `full/` en la MISMA llamada (`createSignedUrls`, una
 * sola request) porque la versión optimizada se sube best-effort y puede no
 * existir: si `opt` falla se usa `full`.
 *
 * `pedidas` es el registro de quien llama (un ref): se marca lo que se pide para no
 * firmarlo dos veces, y se desmarca lo que no se pudo para reintentarlo más adelante.
 * Devuelve las URLs nuevas por id de variante.
 */
export async function firmarMiniaturasDeProductos(
  companyId: string,
  lista: Array<{ id: number | string; imagenKey: string | null; imagenVarianteId: string | null }>,
  pedidas: Set<string>,
): Promise<Record<string, string>> {
  const pendientes = lista.filter((v) => v.imagenKey && !pedidas.has(String(v.id))).slice(0, 60);
  if (!pendientes.length) return {};

  const listas: Record<string, string> = {};
  const aFirmar: { id: string; opt: string; full: string }[] = [];
  for (const v of pendientes) {
    pedidas.add(String(v.id));
    const key = v.imagenKey as string;
    // Productos viejos: la columna guarda la URL entera, no un nombre.
    if (key.startsWith("http")) {
      listas[String(v.id)] = key;
      continue;
    }
    const carpeta = v.imagenVarianteId ?? String(v.id);
    const opt = `${companyId}/${carpeta}/opt/${key}`;
    const full = `${companyId}/${carpeta}/full/${key}`;
    const cacheada = getCachedSignedUrl(opt) || getCachedSignedUrl(full);
    if (cacheada) {
      listas[String(v.id)] = cacheada;
      continue;
    }
    aFirmar.push({ id: String(v.id), opt, full });
  }

  if (aFirmar.length) {
    try {
      const { data } = await supabase.storage
        .from("productos")
        .createSignedUrls(
          aFirmar.flatMap((a) => [a.opt, a.full]),
          3600,
        );
      const firmadas: Record<string, string> = {};
      (data || []).forEach((r: any) => {
        if (r?.signedUrl && r?.path) firmadas[r.path] = r.signedUrl;
      });
      for (const a of aFirmar) {
        const url = firmadas[a.opt] || firmadas[a.full];
        if (!url) {
          // Sin foto en storage: que se pueda reintentar más adelante.
          pedidas.delete(a.id);
          continue;
        }
        setCachedSignedUrl(firmadas[a.opt] ? a.opt : a.full, url);
        listas[a.id] = url;
      }
    } catch {
      aFirmar.forEach((a) => pedidas.delete(a.id));
    }
  }
  return listas;
}
